import { inAutoStack, parentOf, stackDirection } from './stack';
import type {
  GroupLayer,
  HConstraint,
  ImageLayer,
  Layer,
  ShapeLayer,
  TemplateDoc,
  TextLayer,
  VConstraint,
} from './template';

/**
 * Smart resize.
 *
 * Content (fonts, paddings, radii, shadows, image and badge sizes) scales by one factor
 * `s = min(sx, sy)` so nothing gets squashed. The free space this leaves on the longer axis is
 * shared out according to each layer's constraint:
 *
 *   left / top      margin before the layer scales with the content, the layer hugs that edge
 *   right / bottom  same for the opposite edge
 *   center          the layer keeps its offset from the canvas centre
 *   stretch         both margins scale, the layer takes the rest (full-width photos, text boxes)
 *   scale           old behaviour: position and size scale per axis
 *   auto            inferred from the layout: full-bleed layers stretch, everything else keeps
 *                   the ratio between the space before and after it, which preserves the stacking
 *                   order of layers and keeps neighbours from running into each other
 */

/** Resolved behaviour on one axis: an anchor between 0 (start) and 1 (end), stretch or scale. */
export type AxisRule = { kind: 'anchor'; k: number } | { kind: 'stretch' } | { kind: 'scale' };

/** Layers whose shape must not change: stretching would turn a circle into an oval. */
function keepsAspect(layer: Layer): boolean {
  return (
    layer.type === 'qr' ||
    (layer.type === 'shape' && (layer.shape === 'ellipse' || layer.shape === 'triangle'))
  );
}

function autoRule(layer: Layer, pos: number, size: number, total: number): AxisRule {
  const before = pos;
  const after = total - pos - size;
  const edge = total * 0.1;
  if (!keepsAspect(layer) && size >= total * 0.6 && before <= edge && after <= edge) {
    return { kind: 'stretch' };
  }
  const free = before + after;
  if (free <= 0) return { kind: 'anchor', k: 0.5 };
  let k = Math.min(1, Math.max(0, before / free));
  // Snap near-edge and near-centre layers so they stick to the edge / centre exactly
  // (a monotonic mapping, so the stacking order of layers is still preserved)
  if (k < 0.15) k = 0;
  else if (k > 0.85) k = 1;
  else if (Math.abs(k - 0.5) < 0.1) k = 0.5;
  return { kind: 'anchor', k };
}

export function axisRule(
  layer: Layer,
  axis: 'x' | 'y',
  canvas: { width: number; height: number },
): AxisRule {
  const c = layer.constraints;
  const mode: HConstraint | VConstraint = (axis === 'x' ? c?.horizontal : c?.vertical) ?? 'auto';
  switch (mode) {
    case 'left':
    case 'top':
      return { kind: 'anchor', k: 0 };
    case 'center':
      return { kind: 'anchor', k: 0.5 };
    case 'right':
    case 'bottom':
      return { kind: 'anchor', k: 1 };
    case 'stretch':
      return { kind: 'stretch' };
    case 'scale':
      return { kind: 'scale' };
    default:
      return axis === 'x'
        ? autoRule(layer, layer.x, layer.width, canvas.width)
        : autoRule(layer, layer.y, layer.height, canvas.height);
  }
}

/** Human-friendly name of what `auto` resolved to, for the editor UI. */
export function describeAxisRule(rule: AxisRule, axis: 'x' | 'y'): HConstraint | VConstraint {
  if (rule.kind !== 'anchor') return rule.kind;
  if (rule.k === 0) return axis === 'x' ? 'left' : 'top';
  if (rule.k === 1) return axis === 'x' ? 'right' : 'bottom';
  if (rule.k === 0.5) return 'center';
  return 'auto';
}

function placeAxis(
  rule: AxisRule,
  pos: number,
  size: number,
  total: number,
  newTotal: number,
  s: number,
): [number, number] {
  if (rule.kind === 'scale') {
    const f = newTotal / total;
    return [pos * f, size * f];
  }
  const before = pos;
  const after = total - pos - size;
  if (rule.kind === 'stretch') {
    return [before * s, Math.max(1, newTotal - (before + after) * s)];
  }
  // Free space the uniform content scale leaves on this axis (never negative: s <= axis factor)
  const extra = newTotal - total * s;
  return [before * s + rule.k * extra, size * s];
}

const round = (n: number) => Math.round(n * 100) / 100;

/** Resize a document to a new canvas size, adapting every layer (see the module comment). */
export function resizeDoc(doc: TemplateDoc, width: number, height: number): TemplateDoc {
  const sx = width / doc.width;
  const sy = height / doc.height;
  const s = Math.min(sx, sy);
  // Stacks in `auto` direction re-flow (column ↔ row), so their content can keep more of its size.
  // When a stack actually turns (a row becomes a column or back), its text gets the whole width
  // or height instead of a share of it and can grow more.
  const flips = doc.layers.some(
    (l) =>
      l.type === 'group' &&
      l.direction === 'auto' &&
      stackDirection(l, doc) !== stackDirection(l, { width, height }),
  );
  const sMax = Math.max(sx, sy);
  const sFlow = flips ? s * Math.pow(sMax / s, 0.75) : Math.sqrt(sx * sy);
  const contentScale = (layer: Layer) =>
    (layer.type === 'group' && layer.direction === 'auto') || inAutoStack(doc, layer) ? sFlow : s;

  // Top-level layers follow their constraints; layers in a stack keep their offset in the stack
  // (the renderer lays them out anyway) and scale with it.
  const boxes = new Map<string, { x: number; y: number; w: number; h: number }>();
  const boxOf = (layer: Layer): { x: number; y: number; w: number; h: number } => {
    const known = boxes.get(layer.id);
    if (known) return known;
    const parent = parentOf(doc, layer);
    let box;
    if (parent?.direction === 'free') {
      // Placed by hand inside the stack: the offset from its corner scales with the content
      const k = contentScale(layer);
      box = { x: layer.x * k, y: layer.y * k, w: layer.width * k, h: layer.height * k };
    } else if (parent) {
      boxes.set(layer.id, { x: layer.x, y: layer.y, w: layer.width, h: layer.height }); // cycle guard
      const pb = boxOf(parent);
      const k = contentScale(layer);
      box = {
        x: pb.x + (layer.x - parent.x) * k,
        y: pb.y + (layer.y - parent.y) * k,
        w: layer.width * k,
        h: layer.height * k,
      };
    } else {
      const [x, w] = placeAxis(
        axisRule(layer, 'x', doc),
        layer.x,
        layer.width,
        doc.width,
        width,
        s,
      );
      const [y, h] = placeAxis(
        axisRule(layer, 'y', doc),
        layer.y,
        layer.height,
        doc.height,
        height,
        s,
      );
      box = { x, y, w, h };
    }
    boxes.set(layer.id, box);
    return box;
  };

  const layers = doc.layers.map((layer): Layer => {
    const box = boxOf(layer);
    const k = contentScale(layer);
    const keys = layer.motion?.keys;
    const base = {
      ...layer,
      // Key frame offsets are pixels: they shrink and grow with the layer
      ...(keys?.length && {
        motion: {
          ...layer.motion,
          keys: keys.map((key) => ({
            ...key,
            ...(key.x !== undefined && { x: round(key.x * k) }),
            ...(key.y !== undefined && { y: round(key.y * k) }),
          })),
        },
      }),
      x: round(box.x),
      y: round(box.y),
      width: Math.max(1, round(box.w)),
      height: Math.max(1, round(box.h)),
    };
    const shadow =
      'shadow' in layer && layer.shadow
        ? {
            ...layer.shadow,
            x: round(layer.shadow.x * k),
            y: round(layer.shadow.y * k),
            blur: round(layer.shadow.blur * k),
          }
        : null;
    switch (base.type) {
      case 'text': {
        const l = base as TextLayer;
        return {
          ...l,
          fontSize: Math.max(1, round(l.fontSize * k)),
          minFontSize: Math.max(1, round(l.minFontSize * k)),
          letterSpacing: round(l.letterSpacing * k),
          borderRadius: round(l.borderRadius * k),
          strokeWidth: round(l.strokeWidth * k),
          padding: {
            top: round(l.padding.top * k),
            right: round(l.padding.right * k),
            bottom: round(l.padding.bottom * k),
            left: round(l.padding.left * k),
          },
          shadow,
        };
      }
      case 'image': {
        const l = base as ImageLayer;
        return {
          ...l,
          borderRadius: round(l.borderRadius * k),
          borderWidth: round(l.borderWidth * k),
          shadow,
        };
      }
      case 'shape': {
        const l = base as ShapeLayer;
        return {
          ...l,
          borderRadius: round(l.borderRadius * k),
          strokeWidth: round(l.strokeWidth * k),
          shadow,
        };
      }
      case 'group': {
        const l = base as GroupLayer;
        return {
          ...l,
          gap: round(l.gap * k),
          borderRadius: round(l.borderRadius * k),
          padding: {
            top: round(l.padding.top * k),
            right: round(l.padding.right * k),
            bottom: round(l.padding.bottom * k),
            left: round(l.padding.left * k),
          },
        };
      }
      default:
        return base as Layer;
    }
  });
  return { ...doc, width: Math.round(width), height: Math.round(height), layers };
}
